/**
 * Функции для расчета прогноза в YOY Forecast
 */

/**
 * Функция для расчета процента изменения
 * @param {number|string} currentValue - Текущее значение
 * @param {number|string} previousValue - Предыдущее значение
 * @returns {string|null} Процент изменения или null
 */
export const calculatePercentageChange = (currentValue, previousValue) => {
  if (currentValue === 'N/A' || previousValue === 'N/A') return null;
  if (!previousValue || previousValue === 0) return null;
  
  const current = typeof currentValue === 'number' ? currentValue : parseInt(currentValue, 10);
  const previous = typeof previousValue === 'number' ? previousValue : parseInt(previousValue, 10);
  
  if (isNaN(current) || isNaN(previous) || previous === 0) return null;
  
  const change = ((current - previous) / previous) * 100;
  return change > 0 ? `+${change.toFixed(0)}%` : `${change.toFixed(0)}%`;
};

/**
 * Функция для расчета среднего процента роста на основе исторических данных
 * @param {string} asin - ASIN товара
 * @param {Array} historicalWeeks - Исторические недели
 * @param {Function} getSalesData - Функция получения данных о продажах
 * @returns {number} Средний процент роста
 */
export const calculateAverageGrowth = (asin, historicalWeeks, getSalesData) => {
  let totalChange = 0;
  let weeksWithData = 0;
  
  // Проходим по всем историческим неделям
  historicalWeeks.forEach(week => {
    const currentValue = getSalesData(asin, week.year, week.weekNumber);
    const previousValue = getSalesData(asin, week.year - 1, week.weekNumber);
    
    // Проверяем, что у нас есть данные за оба года и предыдущий год не равен 0
    if (currentValue !== 'N/A' && previousValue !== 'N/A' && previousValue !== 0) {
      const change = ((currentValue - previousValue) / previousValue) * 100;
      totalChange += change;
      weeksWithData++;
    }
  });
  
  // Если нет недель с данными, возвращаем 0 (без изменений)
  if (weeksWithData === 0) {
    return 0;
  }
  
  // Рассчитываем средний процент
  return totalChange / weeksWithData;
};

/**
 * Функция для расчета прогноза на основе среднего процента роста
 * @param {string} asin - ASIN товара
 * @param {number} weekNumber - Номер недели
 * @param {number} year - Год
 * @param {number} averageGrowth - Средний процент роста
 * @param {Function} getSalesData - Функция получения данных о продажах
 * @returns {number|string} Прогнозируемое значение или 'N/A'
 */
export const calculateForecast = (asin, weekNumber, year, averageGrowth, getSalesData) => {
  // Получаем данные за прошлый год для этой недели
  const previousYearValue = getSalesData(asin, year - 1, weekNumber);
  
  // Если нет данных за прошлый год, возвращаем 'N/A'
  if (previousYearValue === 'N/A') {
    return 'N/A';
  }
  
  // Рассчитываем прогноз: продажи прошлого года * (1 + средний процент роста / 100)
  const forecastValue = Math.round(previousYearValue * (1 + averageGrowth / 100));
  return forecastValue;
};

/**
 * Функция для получения номера недели по дате (ISO 8601)
 * @param {Date} date - Дата
 * @returns {number} Номер недели
 */
export const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return weekNo;
};

/**
 * Функция для получения даты понедельника указанной недели
 * Результаты кешируются для избежания повторных вычислений
 * @param {number} year - Год
 * @param {number} week - Номер недели
 * @returns {Date} Дата понедельника недели
 */
const _dateOfISOWeekCache = {};
export const getDateOfISOWeek = (year, week) => {
  const cacheKey = `${year}-${week}`;
  if (_dateOfISOWeekCache[cacheKey] !== undefined) {
    return new Date(_dateOfISOWeekCache[cacheKey]);
  }

  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  
  _dateOfISOWeekCache[cacheKey] = ISOweekStart.getTime();
  return ISOweekStart;
};

/**
 * Функция для расчета текущей недели и года
 * @returns {Object} Объект с информацией о текущей неделе
 */
export const getCurrentWeekAndYear = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentWeek = getWeekNumber(now);
  
  // Получаем дату понедельника текущей недели
  const mondayDate = getDateOfISOWeek(currentYear, currentWeek);
  const sundayDate = new Date(mondayDate);
  sundayDate.setDate(mondayDate.getDate() + 6);
  
  return {
    weekNumber: currentWeek,
    year: currentYear,
    mondayDate: mondayDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    sundayDate: sundayDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    // Добавляем объекты Date для расчетов
    mondayDateObj: mondayDate,
    sundayDateObj: sundayDate,
    currentDate: now
  };
};

/**
 * Функция для расчета предыдущих недель (НЕ ВКЛЮЧАЯ текущую неделю)
 * @param {number} currentWeek - Текущая неделя
 * @param {number} currentYear - Текущий год
 * @param {number} count - Количество недель (по умолчанию 8)
 * @returns {Array} Массив исторических недель
 */
export const getHistoricalWeeks = (currentWeek, currentYear, count = 8) => {
  const weeks = [];
  
  // Начинаем с недели ДО текущей (currentWeek - 1)
  for (let i = count; i >= 1; i--) {
    let week = currentWeek - i;
    let year = currentYear;
    
    // Обработка перехода через начало года
    if (week < 1) {
      week += 52;
      year -= 1;
    }
    
    // Получаем даты недели
    const mondayDate = getDateOfISOWeek(year, week);
    const sundayDate = new Date(mondayDate);
    sundayDate.setDate(mondayDate.getDate() + 6);
    
    weeks.push({
      weekNumber: week,
      year: year,
      label: `W${week} ${year}`,
      dateRange: `${mondayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sundayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      // Добавляем объекты Date для расчетов
      mondayDateObj: mondayDate,
      sundayDateObj: sundayDate
    });
  }
  
  return weeks;
};

/**
 * Функция для расчета прогнозных недель
 * @param {number} currentWeek - Текущая неделя
 * @param {number} currentYear - Текущий год
 * @param {number} count - Количество недель (по умолчанию 3)
 * @returns {Array} Массив прогнозных недель
 */
export const getForecastWeeks = (currentWeek, currentYear, count = 3) => {
  const weeks = [];
  
  for (let i = 1; i <= count; i++) {
    let week = currentWeek + i;
    let year = currentYear;
    
    // Обработка перехода через конец года
    if (week > 52) {
      week -= 52;
      year += 1;
    }
    
    // Получаем даты недели
    const mondayDate = getDateOfISOWeek(year, week);
    const sundayDate = new Date(mondayDate);
    sundayDate.setDate(mondayDate.getDate() + 6);
    
    weeks.push({
      weekNumber: week,
      year: year,
      label: `W${week} ${year}`,
      isPrediction: true,
      // Добавляем объекты Date для расчетов
      mondayDateObj: mondayDate,
      sundayDateObj: sundayDate
    });
  }
  
  return weeks;
};